import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import { IEventRequestParams, SortOrder } from '../../../../api/shared.dto';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../../infrastructure/sub-windows';
import { alertServerError, showNotification } from '../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../infrastructure/error-parsing';
import {
    Category,
    FilterField,
    IConfigurationFilter,
    IGenerateEventReportParams,
    IReportConfigItem,
    IReportConfiguration,
    IReportDetailsDTO,
    IReportListItemDTO,
    IReportsDAO,
    IReportsListDTO,
    ISelectableFilter,
    IStartDateTimesDTO,
    IUpdateReportSubscriptionResponse,
    LocalConfigurationStore,
    ReportLayout,
    ReportStatus,
    ReportType,
    TGetReportsSortKeys
} from '../../../../api/reports.dao';
import { IEventDetailsDTO } from '../../../../api/events.dao';
import { ReportForm } from './_report-form/report-form';
import { IAppsyncSubscription, subscribeAppsyncData } from '../../../../infrastructure/utils/appsync.utils';
import { onUpdateAssetMetadata } from '../../../../graphql/subscriptions';
import TrackingServiceProvider from '../../../../tracking-service/tracking-service-provider';
import { OpenFrom, TRACKING_EVENTS } from '../../../../tracking-service/tracking-values';
import { compareDesc, startOfDay } from 'date-fns';
import { utcToZonedTime } from '../../../../infrastructure/utils/date-fns-overrides.utils';
import { DateFnsFormats } from '../../../../infrastructure/date-formatter';
import { DateTimeFormatter } from '../../../../infrastructure/implementations/date-formatter';
import GenerateZoomReportViewModel from './generate-zoom-registration-report-popover/generate-zoom-report.viewmodel';
import {
    IReportGenerationNotificationProps,
    showReportGenerationStartedToast,
    showReportGenerationCompletedToast,
} from './show-report-toast/show-report-toast';
import { IApiDateConverter } from '../../../../infrastructure/api-date-converter';
import { ReportConfigurationViewModel } from './report-configuration/report-configuration.viewmodel';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../api/roles.dao';
import { IGlobalStorage } from '../../../../infrastructure/global-storage';
import { GenerateEventReport } from './_generate-event-report/generate-event-report';
import MeetingsFilterViewModel from '../shared/report-filter/meetings-filter/meetings-filter.viewmodel';
import {
    ATTENDEE_INTERACTION_REPORT_AVAILABLE_FILTERS,
    EVENT_INTERACTIONS_REPORT_AVAILABLE_FILTERS,
    EVENT_REGISTRATION_REPORT_AVAILABLE_FILTERS,
    REGISTRATION_BY_SESSION_REPORT_AVAILABLE_FILTERS
} from '../../../../constants/report-fields.constants';
import { convertMinutesToTime } from '../../../../infrastructure/utils/date.util';
import { IS_REPORT_GENERATING, REPORT_GENERATION_ENDPOINTS, TRACKING_EVENT_REPORTS } from '../../../../constants/api/reports-mapper.constants';
import { IReactIntlProvider } from '../../../../infrastructure/react-intl-provider';
import { NONE } from '../../../../constants/common.constants';
import {
    EDIT_FILE_TEXT,
    FILE_DELETED_TEXT,
    FILE_EDITED_TEXT,
    NEW_FILE_ADDED_TEXT,
    ONE_ROW_PER_ATTENDEE_PER_MEETING_TEXT,
    ONE_ROW_PER_ATTENDEE_TEXT,
    REPORT_GENERATION_COMPLETED_TEXT,
    UPLOAD_FILE_TEXT
} from './report.texts';

type SavedConfigurationType = Pick<IReportConfiguration, 'columns' | 'filters'>;

class ReportsViewModel {
    @mobx.observable
    private loading: boolean;
    @mobx.observable
    public reportsList: IReportDetailsDTO[] = [];
    @mobx.observable
    private reportIdToSubWindowMap: { [index: string]: ISubWindowFacade };
    @mobx.observable
    public sortOrder: SortOrder;
    @mobx.observable
    public sortField: TGetReportsSortKeys;
    @mobx.observable
    public event: IEventDetailsDTO;
    @mobx.observable
    public isGeneratingEventReport: boolean;
    @mobx.observable
    public isGeneratingEventInteractionsReport: boolean;
    @mobx.observable
    public isGeneratingAttendeesInteractionsReport: boolean;
    @mobx.observable
    public isGeneratingRegistrantsBySessionReport: boolean;
    @mobx.observable
    public isGeneratingEventRegistrationReport: boolean;
    private subscriptions: IAppsyncSubscription[];
    @mobx.observable
    public isGenerateReportModalOpen: boolean;
    @mobx.observable
    public isSelectReportModalOpen: boolean;
    @mobx.observable
    private availableDates: { dateNumber: number; dateToRender: string }[] = [];
    @mobx.observable
    public selected: boolean = false;
    @mobx.observable
    public lastSelectedDate: Date | null = null;
    @mobx.observable
    public firstSelectedDate: Date | null = null;
    public isGenerateZoomReportModalOpen: boolean;
    @mobx.observable
    public generateZoomReportVM: GenerateZoomReportViewModel;
    public customFields: IReportConfigItem[] = [];
    public defaultRegistrantsBySessionReportFields: IReportConfigItem[] = [];
    @mobx.observable
    public meetingsFilterVM: MeetingsFilterViewModel;
    public constructor(
        public readonly accountId: number,
        private readonly _event: IEventDetailsDTO,
        @IoC.Inject private readonly errorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly subWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject public readonly reportsDAO = IoC.Container.get(IReportsDAO),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly dateTimeFormatter: DateTimeFormatter = IoC.Container.get(DateTimeFormatter),
        @IoC.Inject private readonly apiDateConverter: IApiDateConverter = IoC.Container.get(IApiDateConverter),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider)
    ) {
        this.reportIdToSubWindowMap = {};
        this.event = this._event;
        this.subscriptions = [];
    }

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    public willUnmount = (): void => {
        this.subscriptions.forEach(el => {
            el?.unsubscribe();
        });
    };

    @mobx.action
    public async initialize(): Promise<void> {
        this.resetSelectedDates();
        const { dates } = await this.getAllStartDateTimes();
        this.setAvailableDates(dates);
    }

    public getMinAvailableDate(): number {
        return this.availableDates.length ? this.availableDates[0].dateNumber : Date.now();
    }

    public getMaxAvailableDate(): number {
        return this.availableDates.length ? this.availableDates[this.availableDates.length - 1].dateNumber : Date.now();
    }

    public fetchCustomFields = async (): Promise<void> => {
        try {
            const responseConfig = await this.reportsDAO.getDefaultEventReportConfiguration({
                ...this.eventReqParams,
                type: ReportType.EVENT_INTERACTIONS_REPORT
            });

            this.customFields = responseConfig.filter(el => el.customField);
            this.defaultRegistrantsBySessionReportFields = responseConfig.filter(el => !Category.INTERACTION.includes(el.category));

        } catch (e) {
            ReportConfigurationViewModel.handleApiException(e);
        }
    };

    @mobx.action
    public fetchReports = async (): Promise<void> => {
        this.loading = true;
        try {
            const result = await this.reportsDAO.getReports({
                accountId: this.accountId,
                eventId: this.event.eventId,
                sortOrder: this.sortOrder,
                sortField: this.sortField,
            });
            this.handleReportsLoaded(result);
        } catch (error) {
            this.onFetchReportsFail(error);
        }
    };

    public addReport = (): ISubWindowFacade => this.showReportForm(
        this.intl.formatMessage(UPLOAD_FILE_TEXT),
        this.intl.formatMessage(NEW_FILE_ADDED_TEXT)
    );

    public getAvailableDates = (): { dateNumber: number; dateToRender: string }[] => {
        return this.availableDates;
    };

    @mobx.action.bound
    public setGenerateReportOpen = (value: boolean): void => {
        this.isGenerateReportModalOpen = value;
    };

    @mobx.action.bound
    public setSelectReportOpen = (value: boolean): void => {
        this.isSelectReportModalOpen = value;
    };

    public openGenerateInteractionsReportModal = (): void => {
        this.initialize();
        this.subWindowsService.showSubWindow({
            isExpandedByDefault: true,
            componentToHost: GenerateEventReport,
            componentToHostProps: {
                vm: this,
                reportType: ReportType.EVENT_INTERACTIONS_REPORT,
                availableFilterTypes: EVENT_INTERACTIONS_REPORT_AVAILABLE_FILTERS
            },
            title: 'Generate Event Interaction Report'
        });
    };

    @mobx.action
    public openGenerateEventRegistrationReportModal = (): void => {
        this.initialize();
        this.subWindowsService.showSubWindow({
            isExpandedByDefault: true,
            componentToHost: GenerateEventReport,
            componentToHostProps: {
                vm: this,
                reportType: ReportType.EVENT_REGISTRATION_REPORT,
                excludedCategories: [Category.MEETING, Category.INTERACTION],
                availableFilterTypes: EVENT_REGISTRATION_REPORT_AVAILABLE_FILTERS
            },
            title: 'Generate Event Registration Report'
        });
    };

    @mobx.action
    public openAttendeesInteractionReportModal = async (): Promise<void> => {
        this.initialize();
        this.meetingsFilterVM = new MeetingsFilterViewModel(
            this.accountId,
            this._event,
            this.intl,
        );
        this.subWindowsService.showSubWindow({
            isExpandedByDefault: true,
            componentToHost: GenerateEventReport,
            componentToHostProps: {
                vm: this,
                reportType: ReportType.ATTENDEE_INTERACTIONS_REPORT,
                availableFilterTypes: ATTENDEE_INTERACTION_REPORT_AVAILABLE_FILTERS,
                withLayout: true
            },
            title: 'Generate Attendees Interaction Report'
        });
    };

    public openGenerateRegistrantsBySessionReportModal = (shouldAutoDownload?: boolean): void => {
        this.initialize();
        this.subWindowsService.showSubWindow({
            isExpandedByDefault: true,
            componentToHost: GenerateEventReport,
            componentToHostProps: {
                vm: this,
                reportType: ReportType.REGISTRANTS_BY_SESSION_REPORT,
                excludedTabs: [],
                excludedCategories: [Category.INTERACTION],
                shouldAutoDownload: shouldAutoDownload,
                availableFilterTypes: REGISTRATION_BY_SESSION_REPORT_AVAILABLE_FILTERS
            },
            title: 'Generate Registrants by Session Report'
        });
    };

    public hasSelectedDateRange(): boolean {
        return !!(this.firstSelectedDate || this.lastSelectedDate);
    }

    @mobx.action.bound
    public selectDates(dates: Date[]): void {
        const [ firstDate, lastDate ] = dates;

        this.firstSelectedDate = firstDate;

        if (lastDate?.getTime() === firstDate?.getTime()) {
            this.lastSelectedDate = null;
        } else {
            this.lastSelectedDate = lastDate;
        }
    }

    @mobx.action
    public selectAll = ({ target: { checked } }: React.ChangeEvent<HTMLInputElement>): void => {
        if (checked) {
            this.firstSelectedDate = new Date(this.getMinAvailableDate());
            this.lastSelectedDate = new Date(this.getMaxAvailableDate());
        } else {
            this.resetSelectedDates();
        }
    };

    @mobx.action
    public clearDates = (): void => {
        this.resetSelectedDates();
    };

    @mobx.action
    public editReport = (reportData: IReportListItemDTO): void => {
        const existingSubWindowForThisMeeting = this.reportIdToSubWindowMap[reportData.assetMetadataId];

        let shallOpenNewSubWindow = true;
        if (existingSubWindowForThisMeeting && existingSubWindowForThisMeeting.getStatus() !== SubWindowStatus.Closed) {
            existingSubWindowForThisMeeting.maximize();
            shallOpenNewSubWindow = false;
        }

        if (shallOpenNewSubWindow) {
            this.reportIdToSubWindowMap[reportData.assetMetadataId] = this.showReportForm(
                this.intl.formatMessage(EDIT_FILE_TEXT),
                this.intl.formatMessage(FILE_EDITED_TEXT),
                reportData
            );
        }
    };

    public closeSubWindow = (id: number): void => {
        if (id) {
            this.reportIdToSubWindowMap[id].close();
        }
    };

    public isReportRunFormShown = (id: number): boolean => {
        return this.reportIdToSubWindowMap[id]
            && this.reportIdToSubWindowMap[id].getStatus() !== SubWindowStatus.Closed;
    };

    public isLoading = (): boolean => {
        return this.loading;
    };

    @mobx.action
    public setSortParams = (sortField: TGetReportsSortKeys, sortOrder: SortOrder): void => {
        this.sortField = sortField;
        this.sortOrder = sortOrder;
        this.fetchReports();
    };

    public downloadFile = async (assetMetadataId: number, fileName: string): Promise<void> => {
        try {
            const { downloadUrl } = await this.reportsDAO.getReportFileDownloadUrl({
                accountId: this.accountId,
                eventId: this.event.eventId,
                assetMetadataId: assetMetadataId
            });
            await this.reportsDAO.getReportFile(downloadUrl, fileName);
            this.trackDownloadFileMixPanel();
            if (fileName.includes('Event Report')) this.trackDownloadEventReport();
        } catch (error) {
            this.showDownloadError(error);
        }
    };

    public getMixinAccountNameAndEventId = (): { [key: string]: string } => ({
        'Account Name': this.globalStorage.getEvent()?.accountName,
        'Event ID': this.event.eventId.toString()
    });

    public trackDownloadFileMixPanel = () => {
        this.trackingService.get().track(TRACKING_EVENTS.DOWNLOAD_REPORT, this.getMixinAccountNameAndEventId());
    };

    public trackGenerateMeetingReportMixPanel = (meetingId: number, eventName:TRACKING_EVENTS): void => {
        const event = this.event;

        this.trackingService.get().track(eventName, {
            'Event ID': event.eventId,
            'Event Name': event.name,
            'Account ID': this.accountId,
            'Account Name': this.globalStorage.getEvent()?.accountName,
            'Meeting ID': meetingId,
        });
    };

    public generateMeetingInteractionsReport = async (
        meetingId: number,
        reportGenerationNotificationProps: IReportGenerationNotificationProps,
    ): Promise<void> => {
        showReportGenerationStartedToast(reportGenerationNotificationProps);
        try {
            const meetingIds = [meetingId];
            const reportConfiguration = this.getMeetingInteractionsConfiguration();
            const { assetMetadataId } = await this.reportsDAO.generateMeetingInteractionsReport({
                accountId: this.accountId,
                eventId: this.event.eventId,
                fields: reportConfiguration
            }, meetingIds);
            this.subscribeReport(assetMetadataId, reportGenerationNotificationProps);
            this.trackGenerateMeetingReportMixPanel(meetingId, TRACKING_EVENTS.GENERATE_MEETING_INTERACTIONS_REPORT);
            if (hasPermission(Permission.REPORTS_TAB)) this.fetchReports();

        } catch (error) {
            this.setGenerationError(error);
        }
    };

    public generateMeetingRegistrationReport = async (
        meetingId: number,
        reportGenerationNotificationProps: IReportGenerationNotificationProps,
    ): Promise<void> => {
        showReportGenerationStartedToast(reportGenerationNotificationProps);
        try {
            const meetingIds = [meetingId];
            const reportConfiguration = this.getMeetingRegistrationConfiguration();
            const { assetMetadataId } = await this.reportsDAO.generateMeetingRegistrationReport({
                accountId: this.accountId,
                eventId: this.event.eventId,
                fields: reportConfiguration
            }, meetingIds);
            this.subscribeReport(assetMetadataId, reportGenerationNotificationProps);
            this.trackGenerateMeetingReportMixPanel(meetingId, TRACKING_EVENTS.GENERATE_MEETING_REGISTRATION_REPORT);
            if (hasPermission(Permission.REPORTS_TAB)) this.fetchReports();
        } catch (error) {
            this.setGenerationError(error);
        }
    };

    public generateReport = async (
        type: ReportType,
        configurationVm: ReportConfigurationViewModel,
        shouldAutoDownload = false,
    ): Promise<boolean> => {
        const {
            addConfigurationToStorage,
            availableFilterTypes,
            getAvailableColumns,
            selectedConfigurationName: configurationName,
            getAppliedFilters,
            getAppliedFilter,
            clearFilter,
            layoutType,
            spliceFilter,
            getUpdatedFilters
        } = configurationVm;

        mobx.runInAction(() => {
            IS_REPORT_GENERATING(this)[type] = true;
        });

        showReportGenerationStartedToast({}, {noStatusIcon: false});
        addConfigurationToStorage();

        let meetingIds = [];
        let updatedFilters = [];
        if (type === ReportType.ATTENDEE_INTERACTIONS_REPORT) {
            const selectedMeetingsFilter = getAppliedFilter(FilterField.MEETING_ID);
            meetingIds = selectedMeetingsFilter.values.map(el => Number(el));
            const splicedFilters = spliceFilter(FilterField.MEETING_ID);
            updatedFilters = getUpdatedFilters(splicedFilters);
        }

        try {
            const { assetMetadataId, assetName } = await REPORT_GENERATION_ENDPOINTS(this)[type](
                this.getReportParams(getAvailableColumns(), type === ReportType.ATTENDEE_INTERACTIONS_REPORT ? updatedFilters : getAppliedFilters()),
                meetingIds,
                layoutType
            );

            this.subscribeReport(assetMetadataId, {}, shouldAutoDownload, assetName);
            if (hasPermission(Permission.REPORTS_TAB)) this.fetchReports();

            this.trackGenerateReportMixPanel(
                TRACKING_EVENT_REPORTS[type],
                availableFilterTypes,
                layoutType,
                configurationName,
                getAppliedFilters()
            );

            this.resetSelectedDates();
            return true;
        } catch (error) {
            this.setGenerationError(error);
        } finally {
            mobx.runInAction(() => {
                IS_REPORT_GENERATING(this)[type] = false;
                if (type === ReportType.ATTENDEE_INTERACTIONS_REPORT) {
                    clearFilter(FilterField.MEETING_ID);
                }
            });
        }
    };

    public subscribeReport = async (
        id: number,
        reportGenerationNotificationProps: IReportGenerationNotificationProps,
        shouldAutoDownload?: boolean,
        assetName?: string
    ): Promise<void> => {
        const subscription = await subscribeAppsyncData<IUpdateReportSubscriptionResponse>(
            onUpdateAssetMetadata,
            { assetMetadataId: id },
            data => {
                const { value: { data: { onUpdateAssetMetadata: updatedReport } } } = data;

                const shouldUnsubscribe = this.updateReportData(
                    id,
                    reportGenerationNotificationProps,
                    updatedReport,
                    shouldAutoDownload,
                    assetName
                );
                if (shouldUnsubscribe) subscription?.unsubscribe();
            },
        );
        this.subscriptions.push(subscription);
    };

    @mobx.action
    public setGenerateZoomReportOpen = (value: boolean, reportType?: ReportType, refetch: boolean = false): void => {
        this.generateZoomReportVM = value ?
            new GenerateZoomReportViewModel(
                this.accountId,
                this._event,
                this.intl,
                () => this.setGenerateZoomReportOpen(false, reportType, true),
                reportType
            ) : null;
        this.isGenerateZoomReportModalOpen = value;
        if (refetch) {
            this.fetchReports();
        }
    };

    public getSortedEmailList(): IReportListItemDTO[] {
        return this.reportsList
            .slice()
            .sort((reportLeft: IReportListItemDTO, reportRight: IReportListItemDTO) => {
                return compareDesc(new Date(reportLeft.uploadDateTime), new Date(reportRight.uploadDateTime));
            });
    }

    private get eventReqParams(): IEventRequestParams {
        return { accountId: this.accountId, eventId: this.event.eventId };
    }

    private loadMeetingInteractionsConfigurationFromStorage = (): IReportConfigItem[] | null => {
        const configurationString = localStorage.getItem(LocalConfigurationStore.EVENT_INTERACTION);
        try {
            const configuration: SavedConfigurationType = JSON.parse(configurationString);
            if (configuration) return 'columns' in configuration ? configuration.columns : configuration;
            else return null;
        } catch (e) {
            return null;
        }
    };

    private loadMeetingRegistrationConfigurationFromStorage = (): IReportConfigItem[] | null => {
        const configurationString = localStorage.getItem(LocalConfigurationStore.REGISTRANT_BY_SESSION);
        try {
            const configuration: SavedConfigurationType = JSON.parse(configurationString);

            if (configuration) {
                if ('columns' in configuration) {
                    return configuration.columns;
                } else {
                    return configuration;
                }
            }
            return null;
        } catch (e) {
            return null;
        }
    };

    private setAvailableDates = (dates: string[]): void => {
        const uniqueDaysDates = [...new Set(dates.map(date => +startOfDay(utcToZonedTime(date, this.event.timeZone))))];
        const availableDates = uniqueDaysDates
        .sort((a, b) => a - b)
        .map((zonedDateNumber: number) => ({
            dateNumber: zonedDateNumber,
            dateToRender: this.dateTimeFormatter.dateToFormattedString(
                new Date(zonedDateNumber),
                DateFnsFormats.StringifiedFullDayMonthNumericDateYear
            )
        }));
        mobx.runInAction(() => {
            this.availableDates = availableDates;
        });
    };

    private showReportForm = (
        title: string,
        notificationLabel: string,
        reportData?: IReportListItemDTO
    ) => this.subWindowsService.showSubWindow({
        componentToHost: ReportForm,
        componentToHostProps: {
            saveCompletedCallback: () => {
                showNotification(notificationLabel);
                this.fetchReports();
            },
            deleteCompletedCallback: () => {
                showNotification(this.intl.formatMessage(FILE_DELETED_TEXT));
                this.fetchReports();
                this.closeSubWindow(reportData?.assetMetadataId);
            },
            eventId: this.event.eventId,
            accountId: this.accountId,
            intl: this.intl,
            reportData: reportData,
        },
        title: title,
    });

    private setGenerationError = (error: Error): void => {
        const errorMessage = this.errorParsing.extractMessage(error);
        alertServerError('API Error generating event reports', errorMessage);
    };
    private showDownloadError = (error: Error): void => {
        const errorMessage = this.errorParsing.extractMessage(error);
        alertServerError('Failed to download report', errorMessage);
    };

    private getMeetingInteractionsConfiguration = (): IReportConfigItem[] | null => {
        const config = this.loadMeetingInteractionsConfigurationFromStorage();
        return config
            ? config.concat(this.customFields).filter(field => field.enabled)
            : null;
    };

    private getMeetingRegistrationConfiguration = (): IReportConfigItem[] => {
        const config = this.loadMeetingRegistrationConfigurationFromStorage();
        return config
            ? config.concat(this.customFields).filter(field => field.enabled)
            : this.defaultRegistrantsBySessionReportFields;
    };

    @mobx.action
    private updateReportData = (
        id: number,
        reportGenerationNotificationProps: IReportGenerationNotificationProps,
        updatedReport: IReportListItemDTO,
        shouldAutoDownload?: boolean,
        assetName?: string,
    ): boolean => {
        const { status, size, objectUrl } = updatedReport;
        const prevReport = this.reportsList.find(({ assetMetadataId }) => assetMetadataId === id);
        const reportIndex = this.reportsList.indexOf(prevReport);

        if (reportIndex !== -1) {
            this.reportsList[reportIndex] = {
                ...prevReport,
                size: size,
                objectUrl: objectUrl,
                status: status
            };
        }

        if (shouldAutoDownload) {
            this.reportsDAO.getReportFileDownloadUrl({
                accountId: this.accountId,
                eventId: this.event.eventId,
                assetMetadataId: id
            })
            .then(({ downloadUrl }) => this.reportsDAO.getReportFile(downloadUrl, assetName))
            .catch(this.showDownloadError);

            showNotification(this.intl.formatMessage(REPORT_GENERATION_COMPLETED_TEXT));
        } else {
            if (status === ReportStatus.READY) {
                showReportGenerationCompletedToast(
                    this.accountId,
                    this.event.eventId,
                    reportGenerationNotificationProps,
                );
                return true;
            }
        }
    };

    private getFormattedFilters = (filters: IConfigurationFilter[]): IConfigurationFilter[] => {
        return filters.map(filter => {
            if (filter.operator === 'MORE_THAN' || filter.operator === 'LESS_THAN') {
                const [value] = filter.values;
                return { ...filter, values: [convertMinutesToTime(value)] };
            }

            return filter;
        });
    };

    private getReportParams = (columnFields?: IReportConfigItem[], filters?: IConfigurationFilter[]): IGenerateEventReportParams => {
        const params: IGenerateEventReportParams = {
            accountId: this.accountId,
            eventId: this.event.eventId,
            timeZone: this.event.timeZone,
            fields: columnFields?.filter(field => field.enabled) || null,
            ...(filters?.length && {
                filter: {
                    logicalOperator: 'AND',
                    filters: this.getFormattedFilters(filters)
                }
            })
        };

        if (this.hasSelectedDateRange()) {
            params.startDateTime = this.apiDateConverter.dateToAPIString(this.firstSelectedDate, this.event.timeZone);
            params.endDateTime = this.apiDateConverter.dateToAPIString(this.lastSelectedDate || this.firstSelectedDate, this.event.timeZone);
        }

        return params;
    };

    private trackDownloadEventReport = (): void => {
        const currentUrl = window.location.href;
        this.trackingService.get().track(TRACKING_EVENTS.DOWNLOAD_EVENT_REPORT, {
            'Open From': OpenFrom.Reports,
            'Account Name': this.globalStorage.getEvent()?.accountName,
            'Event ID': this.event.eventId,
            'Event URL': currentUrl,
        });
    };


    private trackGenerateReportMixPanel = (
        trackingEventName: TRACKING_EVENTS,
        availableFilterTypes: ISelectableFilter[],
        layoutType: ReportLayout,
        configurationName?: string,
        appliedFilters?: IConfigurationFilter[],
    ): void => {

        let filters = [
            this.hasSelectedDateRange() && {
                field: FilterField.MEETING_DATE,
                values: [],
            },
            ...appliedFilters
        ].filter(Boolean)
        .map(filter => availableFilterTypes.find(el => el.field === filter.field).label)
        .join(', ');

        let dataToTrack = {
            ...this.getMixinAccountNameAndEventId(),
            'Configuration name': configurationName || NONE,
            filters: filters || NONE
        };

        if (trackingEventName === TRACKING_EVENTS.GENERATE_ATTENDEES_INTERACTIONS_REPORT) {
            const layoutLabel = layoutType === ReportLayout.ROW_PER_ATTENDEE
                ? this.intl.formatMessage(ONE_ROW_PER_ATTENDEE_TEXT)
                : this.intl.formatMessage(ONE_ROW_PER_ATTENDEE_PER_MEETING_TEXT);

            dataToTrack['Layout'] = layoutLabel;
        }

        this.trackingService.get().track(trackingEventName, dataToTrack);
    };

    @mobx.action
    private handleReportsLoaded = (eventMeetingsDTO: IReportsListDTO) => {
        this.reportsList = eventMeetingsDTO;
        this.loading = false;
    };

    @mobx.action
    private onFetchReportsFail(error): void {
        this.loading = false;
        const errorMessage = this.errorParsing.extractMessage(error);
        alertServerError('API Error fetching reports', errorMessage);
    }

    private getAllStartDateTimes = async (): Promise<IStartDateTimesDTO> => {
        let datesDto: IStartDateTimesDTO = { dates: [] };

        try {
            datesDto = await this.reportsDAO.getMeetingsReportStartDateTimes({
                eventId: this.event.eventId,
                accountId: this.accountId
            });
        } catch (e) {
            alertServerError('Error', this.errorParsing.extractMessage(e));
        }

        return datesDto;
    };

    @mobx.action.bound
    private resetSelectedDates(): void {
        this.firstSelectedDate = null;
        this.lastSelectedDate = null;
    }

}

export default ReportsViewModel;
